import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import get from 'lodash-es/get';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { createLocationFile } from '../../../api/ventura/location';
import { useConfig } from '../../../hooks/useConfig';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { snakeCaseKeys } from '../../../util/object';
import FormEndOptions from '../../forms/FormEndOptions';
import TextField from '../../forms/TextField';
import AutocompleteLocationField from './AutocompleteLocationField';

interface CreateLocationFileFormProps {
  adGroupId: number;
  adGroupName: string;
  onClose: () => void;
}

interface AutocompleteValue {
  label: string;
  value: string;
}

const normalizeAutocompleteLocationValue = (input: AutocompleteValue[]): string =>
  input.map((item) => `${item.label}:${item.value}`).join(';');

const normalizePayload = (form) => {
  const returnValue = snakeCaseKeys({
    ...form,
    chain: normalizeAutocompleteLocationValue(form.chain),
    dma: normalizeAutocompleteLocationValue(form.dma),
    state: normalizeAutocompleteLocationValue(form.state),
    city: normalizeAutocompleteLocationValue(form.city),
  });
  return returnValue;
};

function CreateLocationFileForm({ adGroupId, adGroupName, onClose }: CreateLocationFileFormProps) {
  const { apiHost } = useConfig();
  const { showSnackBar } = useSnackBar();
  const createLocationsFileForm = useForm({
    defaultValues: {
      campaignId: adGroupId,
      chain: [],
      dma: [],
      state: [],
      city: [],
      radius: '',
    },
  });

  const { handleSubmit } = createLocationsFileForm;

  const createLocationFileMutation = useMutation({
    mutationFn: (payload: object) => createLocationFile(apiHost, payload),
    onSuccess: (data) => {
      /* On success, the response will be like this:
        {
          "status": "Location additions are being processed in background.\nyour email for status."
        }
      */
      if (!data.errors) {
        showSnackBar(data.status, 'success', { event: 'location_file_uploaded', ad_group_id: adGroupId });
      } else {
        showSnackBar(data.errors, 'error', { event: 'location_upload_error', ad_group_id: adGroupId });
      }
    },
    onError: (error: Error) => {
      let errorMessage = 'An error occurred. Please try again later.';
      try {
        const errorJson = JSON.parse(error.message);
        errorMessage = get(errorJson, 'errors[0]', errorMessage);
      } finally {
        showSnackBar(errorMessage, 'error', { event: 'location_upload_error', ad_group_id: adGroupId });
      }
    },
  });

  const { isLoading } = createLocationFileMutation;

  const submitHandler = (data) => {
    const payload = normalizePayload(data);
    createLocationFileMutation.mutate(payload);
  };

  return (
    <>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...createLocationsFileForm}>
        <form onSubmit={handleSubmit(submitHandler)}>
          <Stack alignItems="flex-start" spacing={1} padding={2}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Create Location File for {adGroupName}
            </Typography>
            <Stack sx={{ width: '100%' }} spacing={2}>
              <AutocompleteLocationField
                label="Chains"
                name="chain"
                locationType="chain"
                sx={{ width: '100%' }}
                autoHighlight
                rules={{
                  required: 'Please enter at least one chain.',
                }}
                multiple
              />
              <AutocompleteLocationField
                label="City"
                name="city"
                locationType="city"
                sx={{ width: '100%' }}
                autoHighlight
                multiple
              />
              <AutocompleteLocationField
                label="State"
                name="state"
                locationType="state"
                sx={{ width: '100%' }}
                autoHighlight
                multiple
              />
              <AutocompleteLocationField
                label="DMA"
                name="dma"
                locationType="dma"
                sx={{ width: '100%' }}
                autoHighlight
                multiple
              />
              <TextField
                name="radius"
                label="Radius"
                autoComplete="on"
                fullWidth
                autoFocus
                rules={{
                  required: 'Please enter a value.',
                }}
              />
            </Stack>
          </Stack>
          <FormEndOptions>
            <Stack direction="row" justifyContent="center" width="100%" spacing={2}>
              <Button variant="outlined" onClick={onClose}>
                Close
              </Button>
              <Button type="submit" disabled={isLoading} endIcon={isLoading ? <CircularProgress size="1rem" /> : null}>
                Create
              </Button>
            </Stack>
          </FormEndOptions>
        </form>
      </FormProvider>
    </>
  );
}

export default CreateLocationFileForm;
